import { DrmScheme, KeySystems, MediaType, PlaybackOptions } from '@quickplay/player-interface';
import { Logger, PlatformUtil } from '@quickplay/foundation';
import { compareVersions } from './ShakaVersionUtils';

/**
 * Type alias for Shaka library or version parameters
 */
type ShakaLibraryOrVersion = string | { Player?: { version: string } } | undefined;

/**
 * Configuration options for DRM license and certificate setup
 */
interface DrmConfigurationOptions {
  player: any;
  logger: Logger;
  certificate?: ArrayBuffer;
  licenseUrl?: string;
  playbackOptions?: PlaybackOptions;
  shakaLibrary?: any;
  shakaVersion?: string;
  drmScheme?: DrmScheme;
}

/**
 * Gets FairPlay utilities with backward compatibility
 * New location: shaka.drm.FairPlayUtils
 * Old location: shaka.util.FairPlayUtils
 * @param shakaLibrary - Shaka library instance
 * @returns FairPlay utilities object
 */
export const getFairPlayUtils = (shakaLibrary: any): any => {
  // Try new location first (shaka.drm.FairPlayUtils)
  if (shakaLibrary.drm?.FairPlayUtils) {
    return shakaLibrary.drm.FairPlayUtils;
  }

  // Fallback to old location (shaka.util.FairPlayUtils)
  if (shakaLibrary.util?.FairPlayUtils) {
    return shakaLibrary.util.FairPlayUtils;
  }

  throw new Error('FairPlayUtils not found in Shaka Player library');
};

/**
 * Configures Fairplay init data transform for the player
 *
 * @param player - Shaka Player instance to configure
 * @param shakaLibrary - Shaka library instance
 */
export const configureFairplayInitDataTransform = (player: any, shakaLibrary: any): void => {
  const fairPlayUtils = getFairPlayUtils(shakaLibrary);

  player.configure('drm.initDataTransform', (initData: any, initDataType: any, drmInfo: any) => {
    if (initDataType != 'skd') return initData;
    const certificate = drmInfo.serverCertificate;
    const contentId = fairPlayUtils.defaultGetContentId(initData);
    return fairPlayUtils.initDataTransform(initData, contentId, certificate);
  });

  // Register network filters for FairPlay
  player.getNetworkingEngine().registerRequestFilter((type: any, request: any) => {
    if (type === shakaLibrary.net.NetworkingEngine.RequestType.LICENSE && request) {
      request.headers['Content-Type'] = 'application/octet-stream';
      request.headers['Accept'] = 'application/octet-stream, application/json';
    }
  });
};

/**
 * Gets the appropriate key system for the provided DRM scheme
 *
 * @param drmScheme - The DRM scheme to get the key system for
 * @returns The corresponding key system
 */
export const getKeySystemForDrmScheme = (drmScheme: DrmScheme): string => {
  switch (drmScheme) {
    case DrmScheme.FAIRPLAY:
      return KeySystems.FAIRPLAY_1_0;
    case DrmScheme.PLAYREADY:
      return KeySystems.PLAYREADY;
    default:
      return KeySystems.WIDEVINE;
  }
};

/**
 * Configures DRM license and certificate for the player
 *
 * @param options - Configuration options for DRM setup
 */
export const configureLicenseAndCertificate = (options: DrmConfigurationOptions): void => {
  const {
    player,
    logger,
    certificate,
    licenseUrl,
    playbackOptions,
    shakaLibrary,
    shakaVersion,
    drmScheme = DrmScheme.WIDEVINE,
  } = options;

  if (!licenseUrl) return;

  const servers: any = {};
  const keySystem = getKeySystemForDrmScheme(drmScheme);
  const serverCertificate = certificate ? new Uint8Array(certificate) : new Uint8Array();

  const advancedDrmConfiguration = playbackOptions?.advancedDrmConfiguration || {};

  // Normalize robustness values for backward compatibility using runtime detection
  const normalizedAdvancedConfig: any = { ...advancedDrmConfiguration };

  // For Shaka Player >= 4.13.0, always ensure robustness values are set to arrays
  const versionToCheck = shakaVersion || shakaLibrary;

  // Normalize robustness values using consolidated helper function
  normalizedAdvancedConfig.videoRobustness = normalizeRobustnessConfig(
    normalizedAdvancedConfig.videoRobustness,
    versionToCheck,
  );

  normalizedAdvancedConfig.audioRobustness = normalizeRobustnessConfig(
    normalizedAdvancedConfig.audioRobustness,
    versionToCheck,
  );

  const drmAdvancedConfiguration = {
    [keySystem]: {
      serverCertificate,
      ...normalizedAdvancedConfig,
    },
  };

  servers[keySystem] = licenseUrl;

  // Determine default robustness values based on platform and version
  // For Panasonic VIERA TVs with Shaka Player >= 4.13.0, set robustness to empty string
  // to resolve widevine drm playback issues specific to Panasonic platform.
  // Reference: https://github.com/shaka-project/shaka-player/issues/4659
  const shouldOverrideForPanasonic = PlatformUtil.isPanasonicTV() && supportsArrayFormat(versionToCheck);
  const defaultAudioRobustness = shouldOverrideForPanasonic ? '' : playbackOptions?.defaultAudioRobustnessForWidevine;
  const defaultVideoRobustness = shouldOverrideForPanasonic ? '' : playbackOptions?.defaultVideoRobustnessForWidevine;

  const drmConfiguration: any = {
    drm: {
      servers,
      advanced: drmAdvancedConfiguration,
      ...(defaultAudioRobustness !== undefined && { defaultAudioRobustnessForWidevine: defaultAudioRobustness }),
      ...(defaultVideoRobustness !== undefined && { defaultVideoRobustnessForWidevine: defaultVideoRobustness }),
    },
  };

  logger.debug('Server Certificate =>', serverCertificate);
  logger.debug('Drm Configuration =>', drmConfiguration);
  player.configure(drmConfiguration);

  // Configure Fairplay init data transform if needed
  if (drmScheme === DrmScheme.FAIRPLAY && shakaLibrary) {
    configureFairplayInitDataTransform(player, shakaLibrary);
  }
};

/**
 * Gets the mime for safari browser alone as internal player auto detects mime type for all the other browsers.
 * This util should be enhanced in the future.
 *
 * Reason:
 * If the manifest URI does not contain the right extension, playback fails in the safari browser as the
 * internal player detects the mime type from the URI.
 *
 * Also, Yo-Space does not provide the proper URI for HLS content in safari.
 * This is a temporary fix to unblock the customer from having playback issue in safari.
 *
 * Refernce Link:
 * https://v3-0-11-dot-shaka-player-demo.appspot.com/docs/api/lib_player.js.html
 *
 * Note: After Chrome V 113.0.5672.63 get released, Player fails to parse the yospace manifest on the Chrome browser.
 * This happens due to Player reads the manifest as `com` instead of `mpd`
 * We have added the MIME type as `application/dash+xml` for the DASH manifest to fix this issue.
 *
 * @param mediaType
 *
 */
export const getMimeType = (mediaType?: MediaType): string | undefined => {
  switch (mediaType) {
    case MediaType.HLS:
      return 'application/x-mpegurl';
    case MediaType.DASH:
      return 'application/dash+xml';
    default:
      return undefined;
  }
};

/**
 * Determines if the given Shaka Player version supports array format for robustness values
 * @param shakaLibraryOrVersion - Shaka library instance or version string
 * @returns true if version >= 4.13.0, false otherwise
 */
function supportsArrayFormat(shakaLibraryOrVersion: ShakaLibraryOrVersion): boolean {
  if (typeof shakaLibraryOrVersion === 'string') {
    return compareVersions(shakaLibraryOrVersion, '4.13.0') >= 0;
  }

  if (shakaLibraryOrVersion?.Player?.version) {
    return compareVersions(shakaLibraryOrVersion.Player.version, '4.13.0') >= 0;
  }

  // Default to false for safety when version cannot be determined
  return false;
}

/**
 * Parses and validates robustness values from a comma-separated string
 * @param robustnessValues - Comma-separated robustness values
 * @returns Array of trimmed, non-empty values
 */
function parseRobustnessValues(robustnessValues: string): string[] {
  if (!robustnessValues || typeof robustnessValues !== 'string') {
    return [];
  }

  return robustnessValues
    .split(',')
    .map(value => value.trim())
    .filter(value => value.length > 0);
}

/**
 * Normalizes robustness values to the appropriate format based on Shaka Player version.
 * Accepts comma-separated string and converts it to array format if needed.
 *
 * @param robustnessValues - Comma-separated robustness values or undefined
 * @param shakaLibraryOrVersion - Shaka library instance or version string
 * @returns Robustness values in the appropriate format (string or array) for the current Shaka version
 */
export function normalizeRobustnessValue(
  robustnessValues: string | undefined,
  shakaLibraryOrVersion: ShakaLibraryOrVersion,
): string | string[] | undefined {
  // Handle undefined or empty input
  if (!robustnessValues) {
    return supportsArrayFormat(shakaLibraryOrVersion) ? [] : undefined;
  }

  // Parse and validate the input values
  const values = parseRobustnessValues(robustnessValues);

  // Handle empty values after parsing
  if (values.length === 0) {
    return supportsArrayFormat(shakaLibraryOrVersion) ? [] : undefined;
  }

  // Return appropriate format based on version support
  if (supportsArrayFormat(shakaLibraryOrVersion)) {
    // Shaka Player 4.13.0+ supports array format
    return values;
  } else {
    // Older versions (< 4.13.0) only support string format
    return values[0];
  }
}

/**
 * Normalizes a robustness configuration value, handling both defined and undefined cases
 * @param robustnessValue - The robustness value to normalize
 * @param shakaLibraryOrVersion - Shaka library instance or version string
 * @returns Normalized robustness value
 */
function normalizeRobustnessConfig(
  robustnessValue: string | undefined,
  shakaLibraryOrVersion: ShakaLibraryOrVersion,
): string | string[] | undefined {
  if (robustnessValue !== undefined) {
    return normalizeRobustnessValue(robustnessValue, shakaLibraryOrVersion);
  } else if (supportsArrayFormat(shakaLibraryOrVersion)) {
    // For versions >= 4.13.0, set undefined robustness to empty array
    return [];
  }
  return undefined;
}
